import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Film, Play, Plus } from 'lucide-react';
import { getPosterUrl, formatVoteAverage } from '../api/tmdb';
import MovieDetailModal from './MovieDetailModal';
import { useFavorites } from '../hooks/useFavorites';

const MovieCard = ({ movie, onOpenModal = () => {}, variant = 'search', disableInlineModal = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const posterUrl = getPosterUrl(movie.poster_path);
    const { toggleWatchlist, isInWatchlist } = useFavorites();

    const displayTitle = movie.title || movie.name || movie.original_title || movie.original_name || 'Untitled';
    const displayDate = movie.release_date || movie.first_air_date || '';
    const mediaType = movie.media_type ? movie.media_type : (movie.title || movie.original_title ? 'movie' : 'tv');
    const year = (displayDate || '').slice(0, 4);
    const originalLang = (movie.original_language || '').toUpperCase();

    const getGenreName = (genreId) => {
        const genres = {
            28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy',
            80: 'Crime', 99: 'Documentary', 18: 'Drama', 10751: 'Family',
            14: 'Fantasy', 36: 'History', 27: 'Horror', 10402: 'Music',
            9648: 'Mystery', 10749: 'Romance', 878: 'Sci-Fi', 10770: 'TV Movie',
            53: 'Thriller', 10752: 'War', 37: 'Western'
        };
        return genres[genreId] || (mediaType === 'tv' ? 'TV' : 'Movie');
    };

    const handleOpen = () => {
        try { onOpenModal(); } catch (_) {}
        if (disableInlineModal) return;
        setShowModal(true);
    };

    const ratingContainerPos = variant === 'home' ? 'top-3 right-3' : 'bottom-3 right-3';
    const showGenre = variant !== 'home';

    return (
        <>
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ 
                y: 0,
                transition: { type: "spring", stiffness: 300, damping: 20 }
            }}
            whileTap={{ scale: 0.95 }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            onClick={() => setShowModal(true)}
            className="group relative overflow-hidden rounded-md bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-500 cursor-pointer h-full"
        >
            {/* Poster Container */}
            <div className="relative aspect-[2/3] overflow-hidden">
                {/* Poster Image */}
                {posterUrl ? (
                    <motion.img
                        src={posterUrl}
                        alt={displayTitle}
                        className="w-full h-full object-cover"
                        animate={{
                            scale: isHovered ? 1.1 : 1,
                            filter: isHovered ? "brightness(1.05) contrast(1.05)" : "brightness(1) contrast(1)"
                        }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center">
                        <div className="text-center">
                            <Film size={48} className="text-slate-400 mx-auto mb-2" />
                            <span className="text-slate-400 text-sm px-4 leading-relaxed">
                                {displayTitle}
                            </span>
                        </div>
                    </div>
                )}

                {/* Hover black gradient fade overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-80'}`} />

                

                {/* Genre Badge (hidden on home) */}
                {showGenre && (
                    <div className="absolute bottom-3 left-3 flex items-center space-x-1">
                        {movie.genre_ids && movie.genre_ids.length > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-[12px] md:text-sm bg-white/10 border border-white/20 text-white/90">
                                {getGenreName(movie.genre_ids[0])}
                            </span>
                        )}
                    </div>
                )}

                {/* Hover info card */}
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.2 }}
                      className="absolute inset-x-2 bottom-2 rounded-md bg-white/5 backdrop-blur-xl  border border-white/15  p-3"
                      onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
                    >

                      <div className="text-[12px] md:text-sm text-white/90 font-medium flex flex-wrap items-center gap-x-3 gap-y-1">
                        {year && <span>{year}</span>}
                        {mediaType && <span className="opacity-80 capitalize">{mediaType}</span>}
                        {movie.vote_average > 0 && (
                          <span className="inline-flex items-center gap-1 text-[12px]">
                            <Star size={12} className="text-yellow-400 fill-current" /> {formatVoteAverage(movie.vote_average)}
                          </span>
                        )}
                      </div>

                      {movie.overview && (
                        <p className="mt-2 text-white/85 text-[11px] md:text-[12px] leading-snug line-clamp-3">
                          {movie.overview}
                        </p>
                      )}

                      {/* Cast preview only for movies */}
                      {mediaType === 'movie' && movie.credits?.cast?.length > 0 && (
                        <div className="mt-2 flex -space-x-2 overflow-hidden">
                          {movie.credits.cast.slice(0, 5).map((p) => (
                            <img key={p.cast_id || p.id} src={`https://image.tmdb.org/t/p/w92${p.profile_path}`} alt={p.name} className="w-6 h-6 rounded-full border border-white/20 object-cover" />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
            </div>
        </motion.div>
        <AnimatePresence>
          {!disableInlineModal && showModal && (
            <MovieDetailModal
              movieId={movie.id}
              mediaType={mediaType}
              seedItem={movie}
              onClose={() => setShowModal(false)}
            />
          )}
        </AnimatePresence>
        </>
    );
};

export default MovieCard;
